import React, { useMemo } from "react";
import { Box, Typography, Divider, Button, Modal } from "@mui/material";
import { CustomDataGrid, Loading } from "@app/components";
import { SampleRegistrationTypes } from "@app/types/samples.types";
import { generateSampleFlow } from "../../utils/helpers";
import {
  columnsPerformance,
  columnsSampleAudit,
} from "../../utils/sample.columns";
import { AnalysisWhitMeasurement } from "../../config/phases/AnalysisPhases";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

interface AuditModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  loadingAnalysis: boolean;
  sampleFlow?: SampleRegistrationTypes;
  stateSample: any;
  preparationsList: any;
}

const AuditModal: React.FC<AuditModalProps> = ({
  open,
  onClose,
  loading,
  loadingAnalysis,
  sampleFlow,
  stateSample,
  preparationsList,
}) => {
  const memosAudit = useMemo(() => {
    return sampleFlow
      ? generateSampleFlow(sampleFlow, stateSample, preparationsList)
      : {
          flowSample: [],
          flowInitial: [],
          flowAnalysis: [],
          performanceTable: [],
        };
  }, [sampleFlow, stateSample, preparationsList]);

  if (loading || loadingAnalysis) return <Loading />;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="modal-title">
      <Box sx={style}>
        <Typography
          id="modal-title"
          variant="h5"
          component="h2"
          sx={{ mb: 3, fontWeight: "bold" }}
        >
          Trazabilidad{" "}
          {sampleFlow?.masl_reference &&
            ` - Muestra ${sampleFlow.masl_reference}`}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* Sección Muestra */}
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Muestra
        </Typography>
        <CustomDataGrid
          rows={memosAudit?.flowSample}
          columns={columnsSampleAudit}
          search={false}
          disablePagination={true}
          columnVisible={{
            id: false,
          }}
        />

        {/* Fase Inicial */}
        {memosAudit?.flowInitial.length > 0 && (
          <>
            <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 1 }}>
              Fase Inicial
            </Typography>
            <CustomDataGrid
              rows={memosAudit?.flowInitial}
              columns={columnsSampleAudit}
              search={false}
              disablePagination={true}
              columnVisible={{
                id: false,
              }}
            />
          </>
        )}

        {/* Análisis */}
        {memosAudit?.flowAnalysis.length > 0 && (
          <>
            <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 2 }}>
              Análisis
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {memosAudit.flowAnalysis.map((analysis, idxAnalysis) => (
              <Box key={analysis.analysisId} sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "primary.main",
                  }}
                >
                  {`Análisis ${idxAnalysis + 1}: ${
                    analysis.analysisDescription
                  }`}
                  {!sampleFlow?.analysis_type
                    .map((a) => a.id)
                    .includes(analysis.analysisId)
                    ? " (Inactivo)"
                    : ""}
                </Typography>
                {analysis.data.map((preparation, idxPreparation) => (
                  <Box key={preparation.id} sx={{ mb: 3, pl: 2 }}>
                    <CustomDataGrid
                      rows={[
                        {
                          id: preparation.id,
                          fase: "Preparación",
                          analyst: preparation.analyst || "Sin asignar",
                          date: preparation.date || "Fecha no disponible",
                        },
                        ...(
                          (AnalysisWhitMeasurement.includes(
                            analysis.analysisCode
                          )
                            ? preparation.measures
                            : preparation.countings) || []
                        ).map((measure, idxMeasure) => ({
                          id: measure.id,
                          fase: `Medida ${idxMeasure + 1}`,
                          analyst: measure.analyst || "Sin asignar",
                          date: measure.date || "Fecha no disponible",
                        })),
                        ...preparation.countings.flatMap(
                          (counting, idxCounting) => [
                            {
                              id: counting.id,
                              fase: `Contaje ${idxCounting + 1}`,
                              analyst: counting.analyst || "Sin asignar",
                              date: counting.date || "Fecha no disponible",
                            },
                            ...counting.calculations.map((calc, idxCalc) => ({
                              id: calc.id,
                              fase: `Cálculo ${idxCalc + 1}`,
                              analyst: calc.analyst || "Sin asignar",
                              date: calc.date || "Fecha no disponible",
                            })),
                          ]
                        ),
                      ]}
                      columns={columnsSampleAudit}
                      search={false}
                      disablePagination={true}
                      columnVisible={{
                        id: false,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}

        {/* Rendimiento */}
        {memosAudit?.performanceTable.length > 0 && (
          <>
            <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 1 }}>
              Rendimiento
            </Typography>
            <CustomDataGrid
              rows={memosAudit?.performanceTable}
              columns={columnsPerformance}
              search={false}
              disablePagination={true}
              columnVisible={{
                id: false,
              }}
            />
          </>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </Box>
    </Modal>
  );
};

export default AuditModal;
